import { motion } from 'framer-motion';
import {
    BadgeCheck,
    Ban,
    Calendar,
    Check,
    ChevronDown,
    DollarSign,
    Eye,
    FileText,
    Filter,
    Mail,
    Search
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

// Animation variants (reuse pattern)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

const InvoicesList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    dateRange: 'all',
  });
  const [sortBy, setSortBy] = useState('recent');
  const [invoices, setInvoices] = useState([]);
  const [baseInvoices, setBaseInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch invoices for employer
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get('/invoices', { params: { limit: 200 } });
        const raw = res.data?.data || res.data;
        // Map
        const mapped = raw.map((inv) => ({
          id: inv._id,
          invoiceNumber: inv.invoiceNumber,
          employee: {
            id: inv.employee?._id,
            name: `${inv.employee?.firstName || ''} ${inv.employee?.lastName || ''}`.trim(),
            email: inv.employee?.email,
          },
          amount: inv.totalAmount,
          currency: inv.currency || 'USD',
          invoiceDate: inv.invoiceDate,
          dueDate: inv.dueDate,
          status: inv.status,
        }));
        setBaseInvoices(mapped);
        setInvoices(mapped);
      } catch (err) {
        console.error('Failed to fetch invoices', err);
        toast.error('Unable to load invoices');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Filtering + sorting
  useEffect(() => {
    if (loading) return;
    let list = [...baseInvoices];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((inv) =>
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.employee.name.toLowerCase().includes(q) ||
        inv.employee.email?.toLowerCase().includes(q)
      );
    }
    if (filters.status.length) {
      list = list.filter((inv) => filters.status.includes(inv.status));
    }
    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      list = list.filter((inv) => {
        const d = new Date(inv.invoiceDate);
        const diff = (now - d) / (24 * 60 * 60 * 1000);
        if (filters.dateRange === 'today') return diff < 1;
        if (filters.dateRange === 'week') return diff <= 7;
        if (filters.dateRange === 'month') return diff <= 30;
        return true;
      });
    }
    // Sort
    switch (sortBy) {
      case 'amount':
        list.sort((a, b) => b.amount - a.amount);
        break;
      case 'name':
        list.sort((a, b) => a.employee.name.localeCompare(b.employee.name));
        break;
      case 'recent':
      default:
        list.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate));
        break;
    }
    setInvoices(list);
  }, [searchQuery, filters, sortBy, loading, baseInvoices]);

  const handleFilterChange = (type, value) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (type === 'status') {
        updated.status = prev.status.includes(value)
          ? prev.status.filter((s) => s !== value)
          : [...prev.status, value];
      } else {
        updated[type] = value;
      }
      return updated;
    });
  };

  const clearFilters = () => {
    setFilters({ status: [], dateRange: 'all' });
    setSearchQuery('');
  };

  const getStatusStyle = (s) => {
    switch (s) {
      case 'Submitted':
        return 'bg-primary-100 text-primary-700';
      case 'Approved':
        return 'bg-success-100 text-success-700';
      case 'Paid':
        return 'bg-accent-100 text-accent-700';
      case 'Rejected':
        return 'bg-secondary-100 text-secondary-700';
      case 'Draft':
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
  const formatMoney = (amt, cur) => `${cur} ${amt?.toLocaleString()}`;

  const getActiveFilterCount = () => filters.status.length + (filters.dateRange !== 'all' ? 1 : 0);

  // ---- Action handlers ----
  const handleApprove = async (invoiceId) => {
    try {
      await api.put(`/invoices/${invoiceId}/review`, { action: 'approve' });
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, status: 'Approved' } : inv));
      toast.success('Invoice approved');
    } catch (err) {
      console.error('Approve failed', err);
      toast.error(err.response?.data?.message || 'Unable to approve');
    }
  };

  const handleReject = async (invoiceId) => {
    const reason = window.prompt('Enter rejection reason');
    if (!reason) return;
    try {
      await api.put(`/invoices/${invoiceId}/review`, { action: 'reject', rejectedReason: reason });
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, status: 'Rejected' } : inv));
      toast.success('Invoice rejected');
    } catch (err) {
      console.error('Reject failed', err);
      toast.error(err.response?.data?.message || 'Unable to reject');
    }
  };

  return (
    <motion.div className="space-y-6" variants={containerVariants} initial="hidden" animate="visible">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="text-gray-600 mt-1">{invoices.length} invoices from employees</p>
        </div>
      </motion.div>

      {/* Search + Filters */}
      <motion.div variants={itemVariants}>
        <div className="bg-white rounded-xl shadow-soft p-4 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by invoice #, employee name or email"
                className="block w-full pl-10 pr-4 py-2.5 text-gray-900 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary-600 focus:border-primary-600"
              />
            </div>

            {/* Filter + Sort */}
            <div className="flex items-center space-x-2">
              <button onClick={() => setShowFilters(!showFilters)} className="btn-outline flex items-center">
                <Filter size={16} className="mr-2" /> Filters
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 bg-primary-100 text-primary-700 text-xs rounded-full px-2 py-0.5">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>
              <div className="relative">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="select-field py-2.5 pl-4 pr-10 appearance-none bg-white">
                  <option value="recent">Sort by: Recent</option>
                  <option value="amount">Sort by: Amount</option>
                  <option value="name">Sort by: Employee Name</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown size={16} className="text-gray-500" />
                </div>
              </div>
            </div>
          </div>
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="font-medium text-sm text-gray-700">Filter by:</span>
                {getActiveFilterCount() > 0 && (
                  <button onClick={clearFilters} className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
                    Clear all filters
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Status */}
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-700">Status</p>
                  <div className="space-y-2">
                    {['Draft','Submitted','Approved','Rejected','Paid'].map((s) => (
                      <label key={s} className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={filters.status.includes(s)} onChange={() => handleFilterChange('status',s)} className="hidden" />
                        <div className={`w-4 h-4 rounded flex items-center justify-center border ${filters.status.includes(s)?'bg-primary-600 border-primary-600':'border-gray-300'}`}>
                          {filters.status.includes(s) && <Check size={12} className="text-white" />}
                        </div>
                        <span className="text-sm text-gray-700">{s}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {/* Date range */}
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-700">Date Range</p>
                  <div className="space-y-2">
                    {[
                      {value:'all',label:'All Time'},
                      {value:'today',label:'Today'},
                      {value:'week',label:'Last 7 Days'},
                      {value:'month',label:'Last 30 Days'},
                    ].map(opt => (
                      <label key={opt.value} className="flex items-center space-x-2 cursor-pointer">
                        <input type="radio" checked={filters.dateRange===opt.value} onChange={()=>handleFilterChange('dateRange',opt.value)} className="hidden" />
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center border ${filters.dateRange===opt.value?'border-primary-600':'border-gray-300'}`}>{filters.dateRange===opt.value && <div className="w-2 h-2 rounded-full bg-primary-600"></div>}</div>
                        <span className="text-sm text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center items-center h-64"><div className="loading-spinner w-8 h-8 border-4"></div></div>
      ) : invoices.length === 0 ? (
        <motion.div variants={itemVariants} className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4"><FileText size={24} className="text-gray-400" /></div>
          <h2 className="text-xl font-semibold text-gray-900">No invoices found</h2>
          <p className="mt-1 text-gray-500">Try adjusting your search or filters.</p>
          <button onClick={clearFilters} className="mt-4 btn-secondary">Clear Filters</button>
        </motion.div>
      ) : (
        <motion.div variants={itemVariants}>
          <div className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoices.map((inv)=> (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inv.invoiceNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0"><span className="text-primary-700 font-medium">{inv.employee.name.charAt(0)}</span></div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{inv.employee.name}</p>
                            <div className="flex items-center text-xs text-gray-500"><Mail size={12} className="mr-1" />{inv.employee.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"><Calendar size={16} className="inline mr-1 text-gray-500" />{formatDate(inv.invoiceDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"><DollarSign size={16} className="inline mr-1 text-gray-500" />{formatMoney(inv.amount, inv.currency)}</td>
                      <td className="px-6 py-4 whitespace-nowrap"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(inv.status)}`}>{inv.status}</span></td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button onClick={() => navigate(`/invoices/${inv.id}`)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex items-center" title="View Details">
                            <Eye size={16} className="mr-1" />
                            <span className="hidden sm:inline">Details</span>
                          </button>
                          {inv.status==='Submitted' && (
                            <>
                              <button onClick={() => handleApprove(inv.id)} className="p-1.5 rounded-lg hover:bg-gray-100 text-success-600 hover:text-success-700" title="Approve"><BadgeCheck size={16} /></button>
                              <button onClick={() => handleReject(inv.id)} className="p-1.5 rounded-lg hover:bg-gray-100 text-secondary-600 hover:text-secondary-700" title="Reject"><Ban size={16} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default InvoicesList; 